import { State } from '@store/index';
import { useSelector } from 'react-redux';

export const useTheme = () => {
  const theme = useSelector((state: State) => state.flow.theme);

  if (theme === 'light') return lightTheme;
  if (theme === 'dark') return darkTheme;

  return lightTheme;
};

const sharedTheme = {
  shadow: '0px 0px 3px #000000',
  spacing: (multiplier: number) => `${multiplier * 8}px`,
  font: {
    size: {
      xs: '1.2ch',
      sm: '1.5ch',
      md: '1.8ch',
      lg: '2ch',
      xl: '2.4ch',
      xxl: '3ch',
    },
  },
  typography: {
    fontFamily: `Inter`,
  },
  shape: {
    pillRadius: 50,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1700,
    },
  },
};

export const lightTheme = {
  theme: 'light',
  opacity: 1,
  primary: '#3434EF',
  background: {
    lighter: '#F8F7EE',
    light: '#F2F1E7',
    standard: '#FEFDF5',
    dark: '#000000',
    // list and buttons
    primary: '#0400ff10',
    secondary: '#e7e7e789',
    // other
    gradient: (background: string) => background,
    transparent: 'rgba(255, 255, 255, 0.40)',
    hover: 'rgba(0,0,0,0.1)',
    node: '#FEFDF5',
    sidebar: '#FEFDF5',
    header: '#FEFDF5',
    flow: '#FEFDF5',
    inverse: '#000000',
    opposite: '#000000',
  },
  button: {
    background: {
      default: '#F2F2F200',
      green: '#00c08b',
      orange: '#FF7442',
      blue: '#3434EF',
      black: '#000000',
      red: '#f35c5c',
      yellow: '#eba73a',
      purple: '#9a90e9',
      error: '#f44336',
      grey: '#161B22',
      toolbar: '#000000',
    },
    text: {
      default: '#000000',
      green: '#ffffff',
      orange: '#FFFFFF',
      black: '#FFFFFF',
      blue: '#FFFFFF',
      red: '#FFFFFF',
      yellow: '#FFFFFF',
      purple: '#FFFFFF',
      error: '#FFFFFF',
      grey: '#000000',
    },
    soft: {
      background: {
        green: '#00E3AA',
        red: '#f35c5c',
        yellow: '#eba73a',
        blue: '#3434EF',
      },
      text: {
        green: '#FFFFFF',
        red: '#FFFFFF ',
        yellow: '#FFFFFF',
        blue: '#FFFFFF',
      },
    },
  },
  scrollbar: {
    track: '#f0eee4',
    thumb: '#C2C2C2',
  },
  line: {
    lighter: '#f0eee4',
    light: '#E6E3D3',
    standard: '#a5a5a6',
    node: '#E6E3D3',
    outline: '1px solid #3B3B3B',
  },
  soft: {
    background: {
      green: '#00E3AA',
      red: '#f35c5c',
      yellow: '#eba73a',
      blue: '#3434EF',
    },
    color: {
      green: '#FFFFFF',
      red: '#FFFFFF ',
      yellow: '#FFFFFF',
      blue: '#FFFFFF',
    },
  },
  highlight: {
    green: {
      color: '#FEFDF5',
      background: '#000000',
      border: '1px solid #969696',
    },
  },
  telnyx: {
    citron: '#D3FFA6',
    green: '#00E3AA',
    orange: '#FF7442',
    blue: '#3434EF',
    red: '#f35c5c',
    yellow: '#eba73a',
    purple: '#9a90e9',
    error: '#f44336',
    grey: '#161B22',
    deepblue: '#3434EF',
    telGreen: '#00c08b',
    disabled: 'rgba(0, 0, 0, 0.26)',
  },
  grey: {
    light: '#282D33',
    standard: '#0f141b',
    heavy: '#282D33',
    text: '#8b949e',
    textLight: '#6e7681',
  },
  text: {
    standard: '#000000',
    light: '#ffffff',
    subtext: '#999999',
    dark: '#ffffff',
    shadow: '0',
    disabled: 'rgba(0, 0, 0, 0.6)',
    inverse: '#ffffff',
  },
  badge: {
    background: 'rgba(255, 255, 255, 0.40)',
    color: '#FFFFFF',
  },
  ...sharedTheme,
  navIcon: '#6e7681',
  navText: '#8b949e',
  lineBorder: '#2f3744',
  lineBorderDark: '#212730',
  telGreen: '#00E3AA',
  telGreenBackground: '#123633',
  telOrange: '#eb8758',
  telBlue: '#3434EF',
  telDeepBlue: '#3434EF',
  telRed: '#f35c5c',
  telYellow: '#eba73a',
  telPurple: '#9a90e9',
};

export const darkTheme = {
  theme: 'dark',
  opacity: 0.9,
  primary: '#00c08b',
  background: {
    lighter: '#191f27',
    light: '#353740',
    standard: '#161b22',
    dark: '#0f141b',
    primary: '#00e3aa30',
    // primary: '#5858ff30',
    secondary: '#353740',
    // other
    gradient: (background: string) => `linear-gradient(100deg, ${background}, #161b22)`,
    transparent: 'rgba(255, 255, 255, 0.40)',
    hover: 'rgba(255,255,255,0.3)',
    node: '#1F252D',
    sidebar: '#0f141b',
    header: '#0f141b',
    flow: '#151a21',
    inverse: '#ffffff',
    opposite: '#ffffff',
  },
  telnyx: {
    citron: '#D3FFA6',
    green: '#00E3AA',
    orange: '#FF7442',
    blue: '#5858FF',
    red: '#f35c5c',
    yellow: '#eba73a',
    purple: '#9a90e9',
    error: '#f44336',
    grey: '#161B22',
    deepblue: '#3434EF',
    telGreen: '#00c08b',
    disabled: 'rgba(0, 0, 0, 0.26)',
  },
  text: {
    standard: '#ffffff',
    light: '#ffffff',
    dark: '#000000',
    subtext: '#C1C1C1',
    shadow: '0px 0px 4px #000000',
    disabled: 'rgba(255, 255, 255, 0.6)',
    inverse: '#000000',
  },
  scrollbar: {
    track: '#424242',
    thumb: '#686868',
  },
  line: {
    lighter: '#2f3744',
    light: '#2f3744',
    standard: '#2f3744',
    node: '#ffffff20',
    outline: '1px solid #000000',
  },
  soft: {
    background: {
      green: '#123633',
      red: '#341E23',
      yellow: 'rgba(235, 167, 58, .15)',
      blue: 'rgba(88, 166, 255, .15)',
    },
    color: {
      green: '#00E3AA',
      red: '#f35c5c',
      yellow: '#eba73a',
      blue: '#3434EF',
    },
  },
  highlight: {
    green: {
      color: '#00E3AA',
      background: '#123633',
      border: '1px solid #123633',
    },
  },
  button: {
    background: {
      default: '#353740',
      green: '#00c08b',
      orange: '#FF7442',
      blue: '#5858FF',
      black: '#353740',
      red: '#f35c5c',
      yellow: '#eba73a',
      purple: '#9a90e9',
      error: '#f44336',
      grey: '#161B22',
      toolbar: '#ffffff',
    },
    text: {
      default: '#FFFFFF',
      green: '#ffffff',
      orange: '#FFFFFF',
      black: '#FFFFFF',
      blue: '#FFFFFF',
      red: '#FFFFFF',
      yellow: '#000000',
      purple: '#FFFFFF',
      error: '#FFFFFF',
      grey: '#000000',
    },
    soft: {
      background: {
        green: '#00E3AA',
        red: '#f35c5c',
        yellow: '#eba73a',
        blue: '#3434EF',
      },
      text: {
        green: '#FFFFFF',
        red: '#FFFFFF ',
        yellow: '#FFFFFF',
        blue: '#FFFFFF',
      },
    },
  },
  grey: {
    light: '#282D33',
    standard: '#282D33',
    heavy: '#282D33',
    text: '#8b949e',
    textLight: '#6e7681',
  },
  badge: {
    background: '#00000028',
    color: '#FFFFFF',
  },
  ...sharedTheme,
  navIcon: '#6e7681',
  navText: '#8b949e',
  lineBorder: '#2f3744',
  lineBorderDark: '#212730',
  telGreen: '#00E3AA',
  telGreenBackground: '#123633',
  telOrange: '#eb8758',
  telBlue: '#3434EF',
  telDeepBlue: '#3434EF',
  telRed: '#f35c5c',
  telYellow: '#eba73a',
  telPurple: '#9a90e9',
};
